import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AppService } from './app.service';
import { loadSearchParams } from './search-url';

/**
 * Keeps mid-funnel screens off-limits without the search state they describe.
 *
 * A passengers / seats / payment page is built from a picked offer that lives only in memory. On a
 * refresh or a pasted link that state is gone, so the screen used to render with whatever its
 * fields defaulted to — a page that looks like a real booking but describes nothing. This sends the
 * agent to the product's RESULTS screen instead, which itself rebuilds from the URL when the search
 * is there (see core/search-url.ts) and otherwise shows its own empty state.
 *
 * Note the redirect drops no information: the funnel URL never carried the search, so there is
 * nothing to preserve. It is the honest destination rather than a guess at the booking.
 */
export function funnelGuard(product: 'flights' | 'trains' | 'buses' | 'hotels'): CanActivateFn {
  return () => {
    const app = inject(AppService);
    const router = inject(Router);

    const hasState = (() => {
      switch (product) {
        // Flights hold the picked offer in flightReserveCtx; a selected row alone is not enough to
        // reserve, so that is the thing worth guarding on.
        case 'flights': return !!app.flightReserveCtx() || !!app.flightSearchCtx()?.origin;
        case 'trains':  return !!app.trainSearchCtx()?.origin;
        case 'buses':   return !!app.busSearchCtx()?.origin;
        // cityId OR cityCode: tripall keys cities by numeric id, while manual entry and
        // hotel-engine key them by string code. Checking only cityId sent every manual booking
        // back to the search form — the guard decided there was no search when there was one,
        // just not one with a number in it.
        case 'hotels':  return !!app.hotelSearchCtx()?.cityId
                            || !!(app.hotelSearchCtx() as { cityCode?: string } | null)?.cityCode;
      }
    })();

    if (hasState) return true;

    // Redirect to results CARRYING the last search for this product. Without the params the
    // results page had nothing to rebuild from and the agent landed on an empty page after a
    // refresh — the search is in the tab's session precisely for this case.
    const remembered = loadSearchParams(product);
    return remembered
      ? router.createUrlTree([`/${product}-results`], { queryParams: remembered })
      : router.createUrlTree([`/${product}-results`]);
  };
}
